import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import 'moment/locale/es';

import { recuperarConfigUsuari }                 from '../../actions/configUsuariAction';
import { iniciarFormulario }                     from '../../actions/configFormularioAction';
import { esborrarErrorAction, esborrarOkAction } from '../../actions/ui';
import { uiActualizarResultatsAction }           from '../../actions/ui';
import { recuperarHistorial }                    from '../../actions/configAppAction';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (val) =>
    val == null ? '—' : (val % 1 === 0 ? String(val) : (val * 1).toFixed(2));

const CAT_LABELS = ['Hogar','Servicios de Proximidad','Movilidad','Ambiente','Salud','Participación','Comunicación','Oportunidad','Final de Vida'];
const CAT_COLORS = [
  'var(--color-1)',
  'var(--color-2)',
  'var(--color-3)',
  'var(--color-4)',
  'var(--color-5)',
  'var(--color-6)',
  'var(--color-7)',
  'var(--color-8)',
  'var(--color-9)',
];
// ─── Score badge circular ─────────────────────────────────────────────────────
const ScoreBadge = ({ value }) => {
    if (!value || value === '—') return <span style={{ color: '#d1d5db' }}>—</span>;
    const n     = parseFloat(value);
const color =
  n >= 70
    ? 'var(--color-success)'
    : n >= 40
    ? 'var(--color-warning)'
    : 'var(--color-danger)';     return (
        <span style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: 20,
            fontSize: 11, fontWeight: 700,
            backgroundColor: color + '18', color, border: `1px solid ${color}44`,
        }}>
            {value}%
        </span>
    );
};

// ─── GlobalScore circular ─────────────────────────────────────────────────────
const GlobalScore = ({ value }) => {
    if (!value || value === '—') return null;
    const n     = parseFloat(value);
    const color = n >= 70 ? 'var(--color-success)' : n >= 40 ? 'var(--color-warning)' : 'var(--color-danger)';
    const label = n >= 70 ? 'Bueno' : n >= 40 ? 'Medio' : 'Bajo';
    return (
        <div className="mp-global-score">
            <span className="mp-global-score-label">Puntuación global</span>
            <div className="mp-global-score-circle" style={{ borderColor: color, backgroundColor: color + '18' }}>
                <span style={{ color }}>{value}</span>
            </div>
            <span className="mp-global-score-tag" style={{ color }}>{label}</span>
        </div>
    );
};

// ─── Skeleton card ────────────────────────────────────────────────────────────
const SkeletonCard = () => (
    <div style={{
        background: '#fff', borderRadius: 16, border: '1px solid #f3f4f6',
        padding: '20px 24px',
        animation: 'pulse 1.5s ease-in-out infinite',
    }}>
        {[80, 60, 40].map((w, i) => (
            <div key={i} style={{
                height: 12, borderRadius: 6, background: '#f3f4f6',
                width: `${w}%`, marginBottom: i < 2 ? 12 : 0,
            }} />
        ))}
    </div>
);

// ─── Componente principal ─────────────────────────────────────────────────────
export const MenuPrincipal = () => {
    const dispatch  = useDispatch();
    const navegacio = useNavigate();
    const [t]       = useTranslation('generic');
    const { auth, configUsuari, configApp, ui } = useSelector(s => s);
    const { msgError, formulari } = ui;

    const [pagina, setPagina] = useState(1);
    const POR_PAGINA = 8;

    useEffect(() => {
        dispatch(recuperarConfigUsuari(auth.tokenJwt));
    }, [auth]);

    useEffect(() => {
        if (formulari) navegacio('/evaluacion');
        if (ui.msgOk    !== null) setTimeout(() => dispatch(esborrarOkAction()),    10000);
        if (ui.msgError !== null) setTimeout(() => dispatch(esborrarErrorAction()), 10000);
    }, [ui]);

    const handleNuevaEvaluacion = (e) => {
        e.preventDefault();
        dispatch(iniciarFormulario(auth.tokenJwt));
    };

    const handleVerResultats = (row) => {
        dispatch(uiActualizarResultatsAction(row.index));
        navegacio('/resultats');
    };

    // ── Construir filas ────────────────────────────────────────────────────────
    const rows = [];
    if (configApp.contingut?.length) {
        configApp.contingut.forEach((ev, i) => {
            rows.push({
                index: i,
                IdEvaluacion: ev.IdEvaluacion,
                fecha:    moment(ev.DataFin_Evaluacion).format('DD MMM YYYY'),
                fechaRaw: ev.DataFin_Evaluacion,
                global:   fmt(ev.ResultatGlobal),
                cats:     [1,2,3,4,5,6,7,8,9].map(n => fmt(ev[`Resultado_TotalCat${n}`])),
            });
        });
    }

    const total    = rows.length;
    const totalPag = Math.ceil(total / POR_PAGINA);
    const pagRows  = rows.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);
    const cargando = configApp.contingut == null;

    return (
        <>
            {/* Error */}
            {msgError && <div className="mp-alert-error">⚠ {msgError}</div>}

            {/* Toolbar */}
            <div className="mp-toolbar">
                <div>
                    <div className="mp-section-tag">Evaluaciones</div>
                    <h2 className="mp-section-title">Historial de evaluaciones</h2>
                </div>
                <button className="mp-nueva-btn" onClick={handleNuevaEvaluacion}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    {t('Tabla.txtNuevaEvaluacion')}
                </button>
            </div>

            {/* Tarjetas */}
            <div className="mp-cards">
                {cargando ? (
                    Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
                ) : pagRows.length === 0 ? (
                    <div className="mp-empty">
                        <div className="mp-empty-icon">📋</div>
                        <div>No hay evaluaciones todavía.</div>
                        <div style={{ marginTop: 8, fontSize: 12 }}>
                            Pulsa <strong>Nueva evaluación</strong> para empezar.
                        </div>
                    </div>
                ) : (
                    pagRows.map(row => (
                        <div key={row.IdEvaluacion} className="mp-card" onClick={() => handleVerResultats(row)}>
                            <div className="mp-card-top">
                                <div>
                                    <div className="mp-card-fecha">📅 {row.fecha}</div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <GlobalScore value={row.global} />
                                </div>
                            </div>

                            {/* Chips por categoría */}
                            <div className="mp-card-cats">
                                {row.cats.map((val, i) => {
                                    const n     = parseFloat(val);
                                    const color = CAT_COLORS[i];
                                    return (
                                        <span
                                            key={i}
                                            className="mp-cat-chip"
                                            style={{
                                                backgroundColor: color + '12',
                                                color: color,
                                                border: `1px solid ${color}33`,
                                            }}
                                        >
                                            <span className="mp-cat-dot" style={{ background: color }} />
                                            {CAT_LABELS[i]}: {isNaN(n) ? '—' : `${val}%`}
                                        </span>
                                    );
                                })}
                            </div>

                            
                        </div>
                    ))
                )}
            </div>

            {/* Paginación */}
            {totalPag > 1 && (
                <div className="mp-pagination">
                    <button className="mp-pag-btn" onClick={() => setPagina(p => p - 1)} disabled={pagina === 1}>‹</button>
                    {Array.from({ length: totalPag }, (_, i) => i + 1).map(p => (
                        <button
                            key={p}
                            className={`mp-pag-btn${p === pagina ? ' active' : ''}`}
                            onClick={() => setPagina(p)}
                        >
                            {p}
                        </button>
                    ))}
                    <button className="mp-pag-btn" onClick={() => setPagina(p => p + 1)} disabled={pagina === totalPag}>›</button>
                </div>
            )}
        </>
    );
};